import type {
  AutomationCaseChangeDto,
  AutomationCaseResultDto,
  AutomationCaseStatus,
  AutomationClassResultDto,
  AutomationRunComparisonDto,
  AutomationRunResultsDto,
} from '@tms/shared';
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  GitCompare,
  MinusCircle,
  XCircle,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  SelectMenu,
  SelectMenuItem,
  Table,
  TableWrap,
  Th,
} from '../../../components/ui';
import { useT } from '../../../i18n';
import type { MessageKey } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { CauseBadge } from '../ui';
import { useDuration, useRunRefLabel } from './format';

/**
 * A run's results on its page: the totals, the comparison with an earlier run
 * and the classes with their error texts (TestRun src/components/RunResults.tsx).
 * The source is GitLab's test report (JUnit XML) — the pipeline's Tests tab,
 * without leaving for GitLab.
 */

const CASE_META: Record<
  AutomationCaseStatus,
  { label: MessageKey; text: string; badge: string; icon: typeof XCircle }
> = {
  failed: {
    label: 'automation.runs.case.failed',
    text: 'text-fail',
    badge: 'bg-fail-soft text-fail',
    icon: XCircle,
  },
  broken: {
    label: 'automation.runs.case.broken',
    text: 'text-block',
    badge: 'bg-block-soft text-block',
    icon: AlertTriangle,
  },
  skipped: {
    label: 'automation.runs.case.skipped',
    text: 'text-skip',
    badge: 'bg-skip-soft text-skip',
    icon: MinusCircle,
  },
  passed: {
    label: 'automation.runs.case.passed',
    text: 'text-pass',
    badge: 'bg-pass-soft text-pass',
    icon: CheckCircle2,
  },
};

const classLabel = (c: { className: string; displayName?: string | null }) =>
  c.displayName || c.className;

const ErrorMessage = ({ text, className }: { text: string; className: string }) => (
  <p className={cn('whitespace-pre-wrap break-words text-[12px]', className)}>{text}</p>
);

/* ─── the totals ─── */

const Tile = ({
  label,
  value,
  className,
  hint,
}: {
  label: string;
  value: number;
  className: string;
  hint?: string;
}) => (
  <div className={cn('rounded-lg px-4 py-3', className)} title={hint}>
    <p className="text-[26px] font-bold leading-none">{value}</p>
    <p className="mt-1.5 text-[12px] font-medium uppercase tracking-wide">{label}</p>
  </div>
);

export const ResultsSummary = ({ results }: { results: AutomationRunResultsDto }) => {
  const { t } = useT();
  const duration = useDuration();
  const s = results.summary;
  const executed = s.total - s.skipped;
  return (
    <Card>
      <CardHeader
        title={
          <>
            {t('automation.runs.results.title')}{' '}
            <span className="font-normal text-ink-muted">
              · {t('automation.runs.results.passedOf', { passed: s.passed, total: s.total })}
              {s.skipped ? t('automation.runs.results.executed', { count: executed }) : ''}
            </span>
          </>
        }
        action={
          <span className="text-[13px] text-ink-muted">
            {results.final ? '' : `${t('automation.runs.results.preliminary')} · `}
            {t('automation.runs.results.testTime', { duration: duration(s.durationMs) })}
          </span>
        }
      />
      <CardBody>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(110px,1fr))] gap-3">
          <Tile
            label={t('automation.runs.results.passed')}
            value={s.passed}
            className="bg-pass-soft text-pass"
          />
          <Tile
            label={t('automation.runs.results.failed')}
            value={s.failed}
            className="bg-fail-soft text-fail"
          />
          <Tile
            label={t('automation.runs.results.broken')}
            value={s.broken}
            className="bg-block-soft text-block"
            hint={t('automation.runs.results.brokenHint')}
          />
          <Tile
            label={t('automation.runs.results.skipped')}
            value={s.skipped}
            className="bg-skip-soft text-skip"
          />
          <Tile
            label={t('automation.runs.results.total')}
            value={s.total}
            className="bg-sunken text-ink"
          />
        </div>
        {results.suiteErrors.map((e) => (
          <p key={e.name} className="mt-3 flex items-start gap-2 text-[13px] text-block">
            <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            {t('automation.runs.results.suiteError', { name: e.name, error: e.error })}
          </p>
        ))}
      </CardBody>
    </Card>
  );
};

/* ─── the comparison ─── */

type ChangeKind = 'newFailures' | 'fixed' | 'stillFailing' | 'newlySkipped';
const CHANGE_ORDER: ChangeKind[] = ['newFailures', 'fixed', 'stillFailing', 'newlySkipped'];
const CHANGE_STYLES: Record<ChangeKind, { text: string; border: string }> = {
  newFailures: { text: 'text-fail', border: 'border-fail' },
  fixed: { text: 'text-pass', border: 'border-pass' },
  stillFailing: { text: 'text-block', border: 'border-block' },
  newlySkipped: { text: 'text-skip', border: 'border-skip' },
};

export const ComparisonCard = ({
  results,
  against,
  onAgainst,
  classNames,
}: {
  results: AutomationRunResultsDto;
  against?: string | undefined;
  onAgainst: (pipelineId: string | undefined) => void;
  classNames: Record<string, string | null>;
}) => {
  const { t } = useT();
  const refLabel = useRunRefLabel();
  const cmp = results.comparison;
  const [tab, setTab] = useState<ChangeKind>('newFailures');

  const picker =
    results.candidates.length > 0 ? (
      <SelectMenu
        value={against ?? cmp?.against.pipelineId ?? ''}
        onValueChange={(value) => onAgainst(value || undefined)}
        placeholder={t('automation.runs.compare.pick')}
        aria-label={t('automation.runs.compare.pickLabel')}
        className="h-8 w-auto max-w-[340px] text-[13px]"
      >
        {results.candidates.map((ref) => (
          <SelectMenuItem key={ref.pipelineId} value={ref.pipelineId}>
            {refLabel(ref)}
            {ref.ref ? ` · ${ref.ref}` : ''}
          </SelectMenuItem>
        ))}
      </SelectMenu>
    ) : null;

  const title = (text: string) => (
    <span className="flex items-center gap-2">
      <GitCompare className="size-4" aria-hidden="true" />
      {text}
    </span>
  );

  if (!cmp) {
    return (
      <Card>
        <CardHeader title={title(t('automation.runs.compare.title'))} action={picker} />
        <CardBody>
          <p className="text-[13px] text-ink-muted">
            {results.comparisonNote ?? t('automation.runs.compare.nothing')}
          </p>
        </CardBody>
      </Card>
    );
  }

  const items = cmp[tab];
  return (
    <Card>
      <CardHeader
        title={title(t('automation.runs.compare.titleWith', { run: cmp.against.runId }))}
        action={picker}
      />
      <CardBody className="space-y-3">
        <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-2.5">
          {CHANGE_ORDER.map((kind) => {
            const active = tab === kind;
            const count = cmp[kind].length;
            return (
              <button
                key={kind}
                type="button"
                aria-pressed={active}
                onClick={() => setTab(kind)}
                className={cn(
                  'rounded-lg border px-3.5 py-2.5 text-left transition-colors',
                  active
                    ? cn('bg-canvas', CHANGE_STYLES[kind].border)
                    : 'border-border hover:bg-canvas',
                )}
              >
                <span
                  className={cn(
                    'block text-[22px] font-bold leading-none',
                    count ? CHANGE_STYLES[kind].text : 'text-ink-subtle',
                  )}
                >
                  {count}
                </span>
                <span className="mt-1 block text-[12px] text-ink-muted">
                  {t(`automation.runs.compare.${kind}`)}
                </span>
              </button>
            );
          })}
        </div>

        {items.length === 0 ? (
          <p className="text-[13px] text-ink-muted">{t(`automation.runs.compare.${tab}.empty`)}</p>
        ) : (
          <ul className="space-y-2">
            {items.map((item) => (
              <ChangeRow key={item.key} item={item} kind={tab} classNames={classNames} />
            ))}
          </ul>
        )}

        <p className="text-[12px] text-ink-subtle">
          <ComparedNote cmp={cmp} />
        </p>
      </CardBody>
    </Card>
  );
};

const ComparedNote = ({ cmp }: { cmp: AutomationRunComparisonDto }) => {
  const { t } = useT();
  const refLabel = useRunRefLabel();
  const parts = [t('automation.runs.compare.common', { count: cmp.common })];
  if (cmp.notInPrevious) {
    parts.push(
      t('automation.runs.compare.notInPrevious', {
        run: cmp.against.runId,
        count: cmp.notInPrevious,
      }),
    );
  }
  if (cmp.notInCurrent) {
    parts.push(
      t('automation.runs.compare.notInCurrent', {
        run: cmp.against.runId,
        count: cmp.notInCurrent,
      }),
    );
  }
  return <>{`${refLabel(cmp.against)}. ${parts.join(' ')}`}</>;
};

const ChangeRow = ({
  item,
  kind,
  classNames,
}: {
  item: AutomationCaseChangeDto;
  kind: ChangeKind;
  classNames: Record<string, string | null>;
}) => {
  const { t } = useT();
  const meta = CASE_META[item.status];
  const Icon = meta.icon;
  const cls = classNames[item.fqcn] || item.className;
  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-border px-3.5 py-2.5">
      <Icon className={cn('mt-0.5 size-4 shrink-0', meta.text)} aria-hidden="true" />
      <div className="min-w-0 space-y-1">
        <p className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-semibold text-ink">{item.name}</span>
          {kind !== 'fixed' ? <CauseBadge cause={item.cause} /> : null}
        </p>
        <p className="text-[12px] text-ink-muted">{cls}</p>
        {item.message && kind !== 'fixed' ? (
          <ErrorMessage text={item.message} className={meta.text} />
        ) : null}
        {kind === 'newFailures' && item.previous === null ? (
          <p className="text-[12px] text-ink-subtle">{t('automation.runs.compare.wasAbsent')}</p>
        ) : null}
        {kind === 'newFailures' && item.previous === 'skipped' ? (
          <p className="text-[12px] text-ink-subtle">{t('automation.runs.compare.wasSkipped')}</p>
        ) : null}
        {kind === 'newlySkipped' && item.previous ? (
          <p className="text-[12px] text-ink-subtle">
            {t('automation.runs.compare.wasStatus', {
              status: t(CASE_META[item.previous].label).toLowerCase(),
            })}
          </p>
        ) : null}
        {item.previousMessage ? (
          <p className="text-[12px]">
            <span className="text-ink-subtle">
              {kind === 'fixed'
                ? t('automation.runs.compare.failedWith')
                : t('automation.runs.compare.failedOtherwise')}{' '}
            </span>
            <span className="whitespace-pre-wrap break-words text-ink">{item.previousMessage}</span>
          </p>
        ) : null}
      </div>
    </li>
  );
};

/* ─── by class ─── */

type ClassFilter = 'all' | 'red' | 'skipped';

/** Inside a class: failed first, then broken, then skipped. */
const CASE_ORDER: Record<AutomationCaseStatus, number> = {
  failed: 0,
  broken: 1,
  skipped: 2,
  passed: 3,
};

const cell = 'border-b border-border px-3 py-2 align-middle text-ink';
const numCell = cn(cell, 'whitespace-nowrap text-right tabular-nums');

const FilterChip = ({
  active,
  onClick,
  className,
  children,
}: {
  active: boolean;
  onClick: () => void;
  className?: string;
  children: ReactNode;
}) => (
  <button
    type="button"
    aria-pressed={active}
    onClick={onClick}
    className={cn(
      'rounded-md px-2.5 py-1 text-[12px] font-medium transition-colors',
      active
        ? cn('bg-surface shadow-sm', className ?? 'text-ink')
        : 'text-ink-muted hover:text-ink',
    )}
  >
    {children}
  </button>
);

export const ClassesCard = ({ results }: { results: AutomationRunResultsDto }) => {
  const { t } = useT();
  const [filter, setFilter] = useState<ClassFilter>(() =>
    results.classes.some((c) => c.failed + c.broken) ? 'red' : 'all',
  );
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const red = results.classes.filter((c) => c.failed + c.broken > 0);
  const skipped = results.classes.filter((c) => c.skipped > 0);
  const shown = filter === 'red' ? red : filter === 'skipped' ? skipped : results.classes;

  const byClass = new Map<string, AutomationCaseResultDto[]>();
  for (const p of [...results.problems].sort(
    (a, b) => CASE_ORDER[a.status] - CASE_ORDER[b.status],
  )) {
    const list = byClass.get(p.fqcn) ?? [];
    list.push(p);
    byClass.set(p.fqcn, list);
  }
  const toggle = (fqcn: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(fqcn)) next.delete(fqcn);
      else next.add(fqcn);
      return next;
    });

  return (
    <Card>
      <CardHeader
        title={
          <>
            {t('automation.runs.classes.title')}{' '}
            <span className="font-normal text-ink-muted">· {results.classes.length}</span>
          </>
        }
        action={
          <div className="flex gap-1 rounded-lg bg-sunken p-0.5">
            <FilterChip
              active={filter === 'red'}
              onClick={() => setFilter('red')}
              className="text-fail"
            >
              {t('automation.runs.classes.red', { count: red.length })}
            </FilterChip>
            <FilterChip active={filter === 'skipped'} onClick={() => setFilter('skipped')}>
              {t('automation.runs.classes.skipped', { count: skipped.length })}
            </FilterChip>
            <FilterChip active={filter === 'all'} onClick={() => setFilter('all')}>
              {t('automation.runs.classes.all', { count: results.classes.length })}
            </FilterChip>
          </div>
        }
      />
      {shown.length === 0 ? (
        <CardBody>
          <p className="text-[13px] text-ink-muted">{t('automation.runs.classes.none')}</p>
        </CardBody>
      ) : (
        <TableWrap>
          <Table>
            <thead>
              <tr>
                <Th className="w-8">
                  <span className="sr-only">{t('automation.runs.classes.expand')}</span>
                </Th>
                <Th>{t('automation.runs.classes.class')}</Th>
                <Th className="text-right">{t('automation.runs.results.passed')}</Th>
                <Th className="text-right">{t('automation.runs.results.failed')}</Th>
                <Th className="text-right">{t('automation.runs.results.skipped')}</Th>
                <Th className="text-right">{t('automation.runs.results.total')}</Th>
                <Th className="text-right">{t('automation.runs.classes.time')}</Th>
              </tr>
            </thead>
            <tbody>
              {shown.map((c) => (
                <ClassRows
                  key={c.fqcn}
                  c={c}
                  problems={byClass.get(c.fqcn) ?? []}
                  isOpen={open.has(c.fqcn)}
                  onToggle={() => toggle(c.fqcn)}
                />
              ))}
            </tbody>
          </Table>
        </TableWrap>
      )}
    </Card>
  );
};

const ClassRows = ({
  c,
  problems,
  isOpen,
  onToggle,
}: {
  c: AutomationClassResultDto;
  problems: AutomationCaseResultDto[];
  isOpen: boolean;
  onToggle: () => void;
}) => {
  const { t } = useT();
  const duration = useDuration();
  const red = c.failed + c.broken;
  const expandable = problems.length > 0;
  return (
    <>
      <tr
        className={expandable ? 'cursor-pointer hover:bg-canvas' : undefined}
        onClick={expandable ? onToggle : undefined}
      >
        <td className={cell}>
          {expandable ? (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={t('automation.runs.classes.toggle', { name: classLabel(c) })}
              onClick={(event) => {
                event.stopPropagation();
                onToggle();
              }}
              className="rounded p-0.5 text-ink-subtle hover:bg-sunken hover:text-ink"
            >
              {isOpen ? (
                <ChevronDown className="size-4" aria-hidden="true" />
              ) : (
                <ChevronRight className="size-4" aria-hidden="true" />
              )}
            </button>
          ) : null}
        </td>
        <td className={cell}>
          <p className="text-[13px] font-semibold">{classLabel(c)}</p>
          {c.displayName ? (
            <p className="font-mono text-[11.5px] text-ink-subtle">{c.className}</p>
          ) : null}
        </td>
        <td className={cn(numCell, 'text-pass')}>{c.passed}</td>
        <td className={cn(numCell, red ? 'font-semibold text-fail' : 'text-ink-subtle')}>
          {red}
          {c.broken ? (
            <span className="font-normal text-block" title={t('automation.runs.classes.brokenOf')}>
              {' '}
              ({c.broken})
            </span>
          ) : null}
        </td>
        <td className={cn(numCell, c.skipped ? 'text-skip' : 'text-ink-subtle')}>{c.skipped}</td>
        <td className={numCell}>{c.total}</td>
        <td className={cn(numCell, 'text-ink-muted')}>{duration(c.durationMs)}</td>
      </tr>
      {isOpen ? (
        <tr>
          <td className={cell} />
          <td colSpan={6} className={cn(cell, 'pt-0')}>
            <ul className="space-y-2 pb-2.5 pt-1">
              {problems.map((p) => (
                <CaseRow key={p.key} item={p} />
              ))}
            </ul>
          </td>
        </tr>
      ) : null}
    </>
  );
};

const CaseRow = ({ item }: { item: AutomationCaseResultDto }) => {
  const { t } = useT();
  const [showOutput, setShowOutput] = useState(false);
  const meta = CASE_META[item.status];
  const Icon = meta.icon;
  return (
    <li className="flex items-start gap-2.5 rounded-lg border border-border bg-surface px-3.5 py-2.5">
      <Icon className={cn('mt-0.5 size-4 shrink-0', meta.text)} aria-hidden="true" />
      <div className="min-w-0 flex-1 space-y-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-[13px] font-semibold text-ink">{item.name}</span>
          <span className="flex shrink-0 items-center gap-2">
            <CauseBadge cause={item.cause} />
            <span className={cn('rounded-md px-1.5 py-0.5 text-[12px] font-medium', meta.badge)}>
              {t(meta.label)}
            </span>
          </span>
        </div>
        {item.message ? <ErrorMessage text={item.message} className={meta.text} /> : null}
        {item.output ? (
          <Button
            variant="ghost"
            size="sm"
            className="-ml-2.5"
            aria-expanded={showOutput}
            onClick={() => setShowOutput((value) => !value)}
          >
            {showOutput ? (
              <ChevronDown className="size-3.5" aria-hidden="true" />
            ) : (
              <ChevronRight className="size-3.5" aria-hidden="true" />
            )}
            {showOutput
              ? t('automation.runs.classes.hideOutput')
              : t('automation.runs.classes.showOutput')}
          </Button>
        ) : null}
        {showOutput && item.output ? (
          <pre className="max-h-[360px] overflow-auto rounded-md bg-sunken p-3 font-mono text-[12px] leading-relaxed text-ink">
            {item.output}
          </pre>
        ) : null}
      </div>
    </li>
  );
};
